import React from 'react';
import { withStyles, createStyles, Theme } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { Paper, Grid, makeStyles, Box, Divider, CircularProgress } from '@material-ui/core';
import { IAction, IBasketItem } from '../../pages/checkout/[slug]';
import { formatCurrency } from '../../util/currency';
import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import { IDiscountCodeApplied } from './BillingDetails';
import currency from 'currency.js';
import { getCurrentProductPrice } from '../../util/price';

const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      //   backgroundColor: theme.palette.common.black,
      //   color: theme.palette.common.white,
      fontWeight: 'bold',
    },
    body: {
      fontSize: 14,
    },
  }),
)(TableCell);

const StyledTableRow = withStyles((theme: Theme) =>
  createStyles({
    root: {
      '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.background.default,
      },
    },
  }),
)(TableRow);

const useTableStyles = makeStyles({
  root: {
    width: '100%',
    // overflowX: 'auto',
  },
  table: {
    // minWidth: 700,
  },
  productDescription: {
    color: '#aeaeae',
    whiteSpace: 'pre-line',
  },
});

interface IProps {
  items: IBasketItem[];
  loading: boolean;
  discountCodeApplied?: IDiscountCodeApplied;
  dispatch: (action: IAction) => void;
}

export const ProductTable = (props: IProps) => {
  const { items, loading, dispatch, discountCodeApplied } = props;
  const classes = useTableStyles({});
  const price = getCurrentProductPrice(items[0]);

  const subTotal = items.reduce(
    (prev, curr) => currency(prev).add(price.amount * curr.quantity).value,
    0,
  );
  const vatTotal = items.reduce(
    (prev, curr) =>
      currency(prev).add(
        currency(price.amount)
          .multiply(curr.quantity)
          .multiply(curr.vatPercentage * 0.01).value,
      ).value,
    0,
  );
  const grandTotal = subTotal + vatTotal;

  // TODO: Rethink this once we have multiple products in baskets
  const discount = (discountCodeApplied ? discountCodeApplied.discountPercentage : 0.0) / 100.0;

  return (
    <Paper className={classes.root}>
      <Table className={classes.table} aria-label="customized table">
        <TableHead>
          <TableRow>
            <StyledTableCell>Products</StyledTableCell>
            {!loading && (
              <>
                <StyledTableCell align="right">Quantity</StyledTableCell>
                <StyledTableCell align="right">Price</StyledTableCell>
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <StyledTableRow>
              <StyledTableCell style={{ textAlign: 'center' }}>
                <CircularProgress />
              </StyledTableCell>
            </StyledTableRow>
          ) : items.length === 0 ? (
            <StyledTableRow>
              <StyledTableCell>You have no items in your basket.</StyledTableCell>
              <StyledTableCell />
              <StyledTableCell />
            </StyledTableRow>
          ) : (
            items.map((item, index) => (
              <StyledTableRow key={`basket-item-${index}`}>
                <StyledTableCell align="left">
                  <div>
                    <div>{item.name}</div>
                    <div className={classes.productDescription}>{item.description}</div>
                  </div>
                </StyledTableCell>
                <StyledTableCell align="right">
                  <IconButton
                    aria-label="remove-quantity"
                    data-testid="remove-quantity"
                    onClick={() => {
                      dispatch({
                        type: 'REMOVE_QUANTITY',
                        index,
                      });
                    }}
                  >
                    <RemoveIcon />
                  </IconButton>
                  <span style={{ padding: 4 }}>{item.quantity}</span>
                  <IconButton
                    aria-label="add-quantity"
                    data-testid="add-quantity"
                    onClick={() => {
                      dispatch({
                        type: 'ADD_QUANTITY',
                        index,
                      });
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </StyledTableCell>
                <StyledTableCell align="right">
                  {formatCurrency(price.amount, price.currency).multiply(item.quantity).format()}
                </StyledTableCell>
              </StyledTableRow>
            ))
          )}
        </TableBody>
      </Table>
      {items.length > 0 ? (
        <Grid container>
          <Grid item sm={4} xs={'auto'} />
          <Grid item sm={8} xs={12}>
            <Box px={2} py={1} pt={2}>
              <Grid container justifyContent="space-between">
                <Grid item>Subtotal</Grid>
                <Grid item>{formatCurrency(subTotal, price.currency).format()}</Grid>
              </Grid>
            </Box>
            <Box px={2} py={1}>
              <Grid container justifyContent="space-between">
                <Grid item>VAT</Grid>
                <Grid item data-testid="vat-total">
                  {formatCurrency(vatTotal, price.currency).format()}
                </Grid>
              </Grid>
            </Box>
            {discountCodeApplied ? (
              <Box px={2} py={1}>
                <Grid container justifyContent="space-between">
                  <Grid item>
                    {discountCodeApplied.discountPercentage}% Discount{' '}
                    <b>({discountCodeApplied.code.toUpperCase()})</b>
                  </Grid>
                  <Grid item data-testid="vat-total">
                    -{formatCurrency(grandTotal * discount, price.currency).format()}
                  </Grid>
                </Grid>
              </Box>
            ) : null}
            <Divider style={{ margin: '1em' }} />
            <Box px={2} pt={1} pb={3} style={{ fontWeight: 'bold' }}>
              <Grid container justifyContent="space-between">
                <Grid item> Grand Total</Grid>
                <Grid item data-testid="grand-total">
                  {formatCurrency(grandTotal * (1 - discount), price.currency).format()}
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      ) : null}
    </Paper>
  );
};

export default ProductTable;
